import {BaseModel} from '@shared/models/base.model';
import {JobDetailType, JobStatusEnum, JobType, JobActionType, JobActionEnum} from '@generated/graphql';
import {User} from '@modules/user/models/user.model';
import {jobDetailFactory} from '@modules/job/factories/job-detail.factory';
import {userFactory} from '@modules/user/factories/user.factory';
import {Company} from '@modules/company/models/company.model';
import {companyFactory} from '@modules/company/factories/company.factory';
import {Service} from '@modules/service/models/service.model';
import {ServiceSupportedCountry} from '@modules/service/models/service-supported-country.model';
import {serviceFactory} from '@modules/service/factories/service.factory';
import {serviceSupportedCountryFactory} from '@modules/service/factories/service-supported-country.factory';
import {SupportedCountry} from '@modules/supported-country/models/supported-country.model';
import {supportedCountryFactory} from '@modules/supported-country/factories/supported-country.factory';
import {ExtraJob} from '@modules/extra-job/models/extra-job.model';
import {extraJobFactory} from '@modules/extra-job/factories/extra-job.factory';
import {JobObjectDetail} from './job-object-detail.model';
import {Order} from '@modules/order/models/order.model';
import {orderFactory} from '@modules/order/factories/order.factory';
import {startCase} from 'lodash-es';
import {AppStatusType} from '@shared/component/common/status/status.component';
import {fileFactory} from '@modules/file/factories/file.factory';
import {File} from '@app/modules/file/models/file.model';
import {
  DocumentTemplate,
} from "@modules/document-template/models/document-template.model";
import {documentTemplateFactory} from "@modules/document-template/factories/document-template.factory";
import {
  DocumentTemplateTableComponentData
} from "@modules/document-template/components/document-template-table/document-template-table.component";
import { DateTime } from 'luxon';

export interface JobActionWithStyle extends JobActionType {
  icon?: string
  color?: string
}

export const fee = (
  services: OrderDetail[],
  supportedCountry: SupportedCountry,
) => {
  return {
    services,
    supportedCountry,

    get subtotal(): number {
      let subTotal = 0;

      this.services.forEach((item) => {
        // if (!item.service.paymentType.isContactToClient) {
          subTotal += item.quantity * item.service.serviceFee;
        // }
      });

      return subTotal;
    },

    get tax(): number {
      if (this.supportedCountry.includeTax) {
        return (
          this.subtotal * (this.supportedCountry.taxRate / 100)
        );
      }

      return 0;
    },

    get officialFee(): number {
      let officialFee = 0;

      this.services.forEach((item) => {
        // if (!item.service.paymentType.isContactToClient) {
          officialFee += item.quantity * item.service.officialFee;
        // }
      });

      return officialFee;
    },

    get total(): number {
      return (
        this.subtotal +
        this.tax +
        this.officialFee
      )
    }
  }
}

type LaravelSerializedDate = {
  date: string;
  timezone_type: number;
  timezone: string;
};

const isLaravelSerializedDate = (
  value: unknown,
): value is LaravelSerializedDate => {
  if (!value || Array.isArray(value) || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const keys = Object.keys(candidate).sort();

  return (
    keys.length === 3 &&
    keys[0] === 'date' &&
    keys[1] === 'timezone' &&
    keys[2] === 'timezone_type' &&
    typeof candidate['date'] === 'string' &&
    typeof candidate['timezone'] === 'string' &&
    typeof candidate['timezone_type'] === 'number'
  );
};

const normalizeRawDetailDates = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeRawDetailDates(item));
  }

  if (isLaravelSerializedDate(value)) {
    return (
      DateTime.fromSQL(value.date, { zone: value.timezone || 'UTC' }).toISO() ??
      value.date
    );
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, childValue]) => {
      result[key] = normalizeRawDetailDates(childValue);

      return result;
    }, {});
  }

  return value;
};

export class Job extends BaseModel implements JobType {
  id: string;
  matter: string;
  detail: JobDetailType;
  detailObject: JobObjectDetail;
  experts: User[];
  customers: User[];
  participants: User[];
  status: JobStatusEnum;
  company: Company;
  statusLabel: string;

  supportedCountry: SupportedCountry;
  service: Service;
  serviceSupportedCountry: ServiceSupportedCountry;
  companyId?: string;
  createdBy: User;
  description?: string;
  extraJobs: ExtraJob[];
  order?: Order;
  _model: string;
  estimatedTime: string;
  canClose: boolean;
  canConfirm: boolean;
  documents: File[];
  parentId?: string;
  parent?: InheritJob;
  documentTemplates: DocumentTemplate[];
  noPendingActionLabel: string
  actions: JobActionWithStyle[]
  canConfirmBilling: boolean

  // payments: Payment[]

  constructor(state: JobType) {
    super(state);

    this.detail = state.detail ? jobDetailFactory.create(state.detail) : null;

    if (state.rawDetail) {
      const parsedDetail = JSON.parse(state.rawDetail) || {};
      const normalizedDetail = normalizeRawDetailDates(
        parsedDetail,
      ) as ConstructorParameters<typeof JobObjectDetail>[0];

      this.detailObject = new JobObjectDetail(normalizedDetail);
    }

    this.customers = state.customers
      ? state.customers.map((customer) => userFactory.create(customer))
      : [];

    this.experts = state.experts
      ? state.experts.map((expert) => userFactory.create(expert))
      : [];

    this.participants = state.participants
      ? state.participants.map((participant) => userFactory.create(participant))
      : [];

    this.company = state.company ? companyFactory.create(state.company) : null;

    this.supportedCountry = state.supportedCountry
      ? supportedCountryFactory.create(state.supportedCountry)
      : null;

    this.service = state.service ? serviceFactory.create(state.service) : null;

    this.serviceSupportedCountry = state.serviceSupportedCountry
      ? serviceSupportedCountryFactory.create(state.serviceSupportedCountry)
      : null;

    this.createdBy = state.createdBy
      ? userFactory.create(state.createdBy)
      : null;

    this.extraJobs = state.extraJobs
      ? state.extraJobs.map((item) => extraJobFactory.create(item))
      : [];

    this.order = state.order ? orderFactory.create(state.order) : null;

    this.documents = fileFactory.createEntities(state.documents);

    this.documentTemplates = documentTemplateFactory.createEntities(state.documentTemplates)

    if (state.parent) {
      this.parent = new InheritJob(state.parent);
    }

    this.actions = state.actions?.map(action => {
      return {
        ...action,
        color: 'warn',
        icon: ''
      } as JobActionWithStyle
    }) ?? []
  }

  get scopeOfWork(): string {
    return this.serviceSupportedCountry?.scopeOfWork ?? 'None';
  }

  get fee(): ReturnType<typeof fee> {
    return fee(
      this.services,
      this.supportedCountry
    )
  }

  getModelName(): string {
    return Job.name;
  }

  isOngoing(): boolean {
    return this.status === JobStatusEnum.Ongoing;
  }

  getLongMatter(): string {
    return [this.matter, this.company?.name]
      .filter((item) => !!item)
      .join(' - ');
  }

  getMatterDisplay(): string {
    return this.matter ? startCase(this.matter) : '';
  }

  public get createdDate() {
    return this.createdAt;
  }

  public renderStatusStyle(): AppStatusType {
    switch (this.status) {
      case JobStatusEnum.New:
        return 'new';
      case JobStatusEnum.Ongoing:
        return 'new-light';
      case JobStatusEnum.Done:
        return 'success';
      case JobStatusEnum.Pending:
        return 'hold';
      case JobStatusEnum.Draft:
        return 'hold';
      case JobStatusEnum.Closed:
        return 'cancelled';
      case JobStatusEnum.Unassigned:
        return 'action-light';
      default:
        return 'default';
    }
  }

  renderStatusStyleByUser(user: User): AppStatusType {
    const order = this.parent ? this.parent?.order : this.order

    switch (this.status) {
      case JobStatusEnum.Draft: {
        return 'hold'
      }
      case JobStatusEnum.Completed:
      case JobStatusEnum.Done: {
        return 'success'
      }
      case JobStatusEnum.Closed: {
        return 'cancelled'
      }
    }

    if (user.isCustomer()) {
      switch (this.status) {
        case JobStatusEnum.Kyc:
          return 'hold';
        case JobStatusEnum.Payment:
          if (order?.payment?.isPaid) {
            return 'success'
          }

          if (order?.payment?.isReviewingPayment) {
            return 'purple'
          }

          if (order?.payment?.isPaymentToDo) {
            return 'hold'
          }

          return 'purple'
        default: {
          return 'purple'
        }
      }
    } else if (user.isCorporateExpert()) {
      switch (this.status) {
        case JobStatusEnum.BillingToConfirm:
        case JobStatusEnum.Reviewing:
        case JobStatusEnum.KycReviewing:
        case JobStatusEnum.PreparingTask:
        case JobStatusEnum.FillingDocuments: {
          return 'hold'
        }
        case JobStatusEnum.Payment:
          if (order?.payment?.isReviewingPayment) {
            return 'hold'
          }

          return 'purple'
        default: {
          return 'purple'
        }
      }
    }

    return 'purple'
  }

  renderStatusLabel(): string {
    return startCase(this.status.toLowerCase());
  }

  get templateData(): DocumentTemplateTableComponentData {
    return {
      showGenerateDocumentBtn: true,
      templateData: {selectedId: this.id},
      showLabel: false,
      hasDocumentTemplateId: this.serviceSupportedCountry.id,
      hasDocumentTemplateType: this.serviceSupportedCountry._model,
      showUpdate: false,
      showDownload: false
    }
  }

  get services(): OrderDetail[] {
    const mainServices = this.detailObject?.jobServiceOrder?.mainServices ?? [];
    const additionalServices = this.detailObject?.jobServiceOrder?.additionalServices ?? [];

    return mainServices.concat(additionalServices);
  }

}

export interface JobServicesOrder {
  mainServices: OrderDetail[];
  additionalServices: OrderDetail[];
  // currency: string,
  // taxRate: number
}

export interface OrderDetail {
  service: ServiceSupportedCountry;
  quantity: number;
}

export class InheritJob extends Job {
}
